<script setup lang="ts">
import type { RankEntry } from "@/types/Ranking";
import iconHouse from "@/assets/images/icon_house.png";
import iconMansion from "@/assets/images/icon_mansion.png";

const props = defineProps<{
  houseRanks: RankEntry[];
  mansionRanks: RankEntry[];
}>();

const blocks = computed(() => [
  {
    key: "house",
    icon: iconHouse,
    heading: "戸建て向け　ランキング",
    items: props.houseRanks,
    buttonLabel: "【戸建て】各社の口コミを見る",
    anchor: "#review-house",
  },
  {
    key: "mansion",
    icon: iconMansion,
    heading: "マンション向け　ランキング",
    items: props.mansionRanks,
    buttonLabel: "【マンション】各社の口コミを見る",
    anchor: "#review-mansion",
  },
]);
</script>

<template>
  <section class="ranking">
    <div class="ranking-container">
      <div v-for="block in blocks" :key="block.key" :class="['housingBlock', `housingBlock--${block.key}`]">
        <div :class="['housing-heading', `housing-heading--${block.key}`]">
          <img :class="['housing-heading-icon', `housing-heading-icon--${block.key}`]" :src="block.icon" alt="" />
          <h2 class="housing-heading-text">{{ block.heading }}</h2>
        </div>
        <ul class="ranking-list">
          <li
            v-for="item in block.items"
            :key="item.name"
            class="ranking-item"
            :class="`ranking-item--rank${item.rank <= 3 ? item.rank : 'low'}`"
          >
            <AtomsRankingMedal :rank="item.rank" context="ranking" />
            <span class="ranking-item-company">{{ item.name }}</span>
          </li>
          <li class="ranking-item ranking-item--button">
            <a
              class="ranking-button"
              :href="block.anchor"
              v-smooth-scroll="{ duration: 300, offset: -60 }"
            >
              <span class="ranking-button-label">{{ block.buttonLabel }}</span>
              <span class="ranking-button-arrow" aria-hidden="true" />
            </a>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.ranking {
  padding: 0 15px 38px;
}

.ranking-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.housingBlock {
  border: 1px solid $colorGray;
  border-radius: 4px;
  overflow: hidden;
}

.housing-heading {
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: $colorLightBlue;
  border-top: 4px solid #1c79c6;
  border-bottom: 1px solid #1c79c6;
  box-sizing: border-box;
}

.housing-heading--house {
  padding: 10px 20px;
}

.housing-heading--mansion {
  padding: 10px 23px;
}

.housing-heading-icon {
  height: auto;
  flex: none;
}

.housing-heading-icon--house {
  width: 22px;
}

.housing-heading-icon--mansion {
  width: 15px;
}

.housing-heading-text {
  padding: 0 0 0 5px;
  font-size: px(18);
  font-weight: 700;
  color: $colorBasicText;
}

.ranking-list {
  display: flex;
  flex-direction: column;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  min-height: 80px;
  box-sizing: border-box;
  border-bottom: 1px solid $colorGray;

  // 1〜3位は下→上の淡いグラデ背景
  &--rank1 {
    background: linear-gradient(0deg, #ffffff 0%, #fff1d3 100%);
  }
  &--rank2 {
    background: linear-gradient(0deg, #ffffff 0%, #e8f1f4 100%);
  }
  &--rank3 {
    background: linear-gradient(0deg, #ffffff 0%, #ffefe9 100%);
  }
  // 4位以降は余白を詰めて白背景
  &--ranklow {
    padding: 8px 16px;
    min-height: 0;
  }
}

.ranking-item-company {
  font-size: px(15);
  font-weight: 700;
  color: $colorBasicText;
}

.ranking-item--button {
  padding: 16px;
  min-height: 0;
  border-bottom: none;
}

.ranking-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 6px 0 8px;
  border-radius: 4px;
  background-color: $colorBlue;
  box-shadow: 0px 2px 0px 0px rgba(0, 0, 0, 0.16);
  text-decoration: none;
  box-sizing: border-box;
}

.ranking-button-label {
  font-size: px(14);
  color: #fff;
  line-height: 24px;
}

.ranking-button-arrow {
  width: 9px;
  height: 9px;
  border-right: 2px solid #fff;
  border-bottom: 2px solid #fff;
  transform: translateY(-3px) rotate(45deg);
  flex: none;
}

@include media(pc) {
  .ranking {
    padding: 0 0 52px;
  }

  .ranking-container {
    flex-direction: row;
    gap: 16px;
  }

  .housingBlock {
    flex: 1;
    min-width: 0;
  }

  .ranking-item--button {
    flex: 1;
  }

  .ranking-list {
    height: calc(100% - 53px);
  }
}
</style>
